import React, { useEffect, useState } from "react";
import httpClient from "../../_util/api";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
  Checkbox, FormControlLabel, FormControl, InputLabel, Select, MenuItem,
  Stack, InputAdornment, Tooltip, Paper,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import ButtonMain from "../Button";

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

const EDIT_INITIAL_FORM = {
  email: '',
  CNICNo: '',
  firstName: '',
  lastName: '',
  gender: '',
  address: '',
  town: '',
  country: '',
  phoneNo_Mobile: '',
  phoneNo_Home: '',
  presentHouseholdIncome: '',
  totalFamilyMembers: '',
  fatherName: '',
  motherName: '',
  orphan: '',
  signature: '',
  referenceName1: '',
  referenceEmail1: '',
  referencePhone1: '',
  referenceName2: '',
  referenceEmail2: '',
  referencePhone2: '',
};

const Section = ({ title, children }) => (
  <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 3, mb: 3 }}>
    <Typography variant="subtitle1" fontWeight="bold" color="#208146" mb={2}>
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Paper>
);

const Row = ({ children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
    {children}
  </Stack>
);

// Same 00000-0000000-0 mask used in ScholarshipForm's CNIC field
const formatCNIC = (raw) => {
  const value = String(raw || "").replace(/[^0-9]/g, "");
  let formattedValue = "";

  if (value.length > 5) {
    formattedValue += value.substring(0, 5) + "-";
    if (value.length > 12) {
      formattedValue += value.substring(5, 12) + "-";
      formattedValue += value.substring(12, 13);
    } else {
      formattedValue += value.substring(5);
    }
  } else {
    formattedValue = value;
  }

  return formattedValue;
};

const generateFirstSixAt123 = (student) => {
  const firstName = student?.firstName;
  const lastName = student?.lastName;
  const email = student?.email;

  const joinedName = (String(firstName || "") + String(lastName || "")).replace(/\s+/g, "");
  const emailLocal = String(email || "").split("@")[0] || "";
  const base = joinedName || emailLocal || "student";

  const lettersOnly = base.replace(/[^A-Za-z]/g, "") || "student";
  const six = (lettersOnly + "xxxxxx").substring(0, 6);
  return `${six}@123`;
};

// Full student profile edit (mirrors the student's own /student/my-profile),
// plus editable email/CNIC and an optional password change. Shared so the
// same edit UI is available both from the Students list and from an
// application's "Take Action" screen (student id there is applicationData.userId).
const EditStudentModal = ({ open, student, onClose, onSaved, onNotify, allowPasswordChange = true }) => {
  const [editForm, setEditForm] = useState(EDIT_INITIAL_FORM);
  const [changePassword, setChangePassword] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEditForm({
      email: student?.email || "",
      CNICNo: formatCNIC(student?.CNICNo),
      firstName: student?.firstName || "",
      lastName: student?.lastName || "",
      gender: GENDER_OPTIONS.find(g => g.toLowerCase() === (student?.gender || "").toLowerCase()) || "",
      address: student?.address || "",
      town: student?.town || "",
      country: student?.country || "",
      phoneNo_Mobile: student?.phoneNo_Mobile || "",
      phoneNo_Home: student?.phoneNo_Home || "",
      presentHouseholdIncome: student?.presentHouseholdIncome ?? "",
      totalFamilyMembers: student?.totalFamilyMembers ?? "",
      fatherName: student?.fatherName || "",
      motherName: student?.motherName || "",
      orphan: student?.orphan || "",
      signature: student?.signature || "",
      referenceName1: student?.referenceName1 || "",
      referenceEmail1: student?.referenceEmail1 || "",
      referencePhone1: student?.referencePhone1 || "",
      referenceName2: student?.referenceName2 || "",
      referenceEmail2: student?.referenceEmail2 || "",
      referencePhone2: student?.referencePhone2 || "",
    });
    setChangePassword(false);
    setEditPassword(generateFirstSixAt123(student));
    setEditError("");
  }, [open, student]);

  const handleEditChange = (field) => (e) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleClose = () => {
    setEditError("");
    onClose?.();
  };

  const handleSave = async () => {
    const studentId = student?._id;
    if (!studentId) {
      setEditError("Missing student id — cannot save");
      return;
    }
    if (!String(editForm.email).trim()) {
      setEditError("Email is required");
      return;
    }
    if (!String(editForm.CNICNo).trim()) {
      setEditError("CNIC is required");
      return;
    }
    if (changePassword && !editPassword) {
      setEditError("Password required");
      return;
    }
    setEditError("");
    setEditSaving(true);
    try {
      const payload = {
        ...editForm,
        email: String(editForm.email).trim(),
        CNICNo: String(editForm.CNICNo).trim(),
        changePassword,
        ...(changePassword ? { newPassword: editPassword } : {}),
      };
      // Number fields: drop when left blank so the backend doesn't cast "" -> NaN
      ['presentHouseholdIncome', 'totalFamilyMembers'].forEach((k) => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      const res = await httpClient.put(`/user/student-info/${studentId}`, payload);
      if (res?.status === 200) {
        onNotify?.("success", res.data?.message || "Student updated");
        onSaved?.(res.data?.student || payload);
        onClose?.();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update student";
      setEditError(msg);
      onNotify?.("error", msg);
    } finally {
      setEditSaving(false);
    }
  };

  const studentName = `${student?.firstName || ""} ${student?.lastName || ""}`.trim();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Edit student — {studentName}</DialogTitle>
      <DialogContent sx={{ bgcolor: "#fafafa" }}>
        {editError && (
          <Typography color="error" sx={{ pt: 1, pb: 1 }}>{editError}</Typography>
        )}

        <Section title="Account Information">
          <Row>
            <TextField
              label="Email"
              type="email"
              value={editForm.email}
              onChange={handleEditChange('email')}
              fullWidth
            />
            <TextField
              label="CNIC Number"
              placeholder="00000-0000000-0"
              value={editForm.CNICNo}
              onChange={(e) => setEditForm((prev) => ({ ...prev, CNICNo: formatCNIC(e.target.value) }))}
              inputProps={{ maxLength: 15 }}
              fullWidth
            />
          </Row>

          {allowPasswordChange && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={changePassword}
                    onChange={(e) => setChangePassword(e.target.checked)}
                  />
                }
                label="Also change password"
              />
              {changePassword && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 0.5 }}>
                    Default: first 6 letters of <strong>first + last name</strong> (no spaces) + <strong>@123</strong> — editable below
                  </Typography>
                  <TextField
                    label="Password"
                    type="text"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    fullWidth
                  />
                </>
              )}
            </>
          )}
        </Section>

        <Section title="Personal Information">
          <Row>
            <TextField label="First Name" value={editForm.firstName} onChange={handleEditChange('firstName')} fullWidth />
            <TextField label="Last Name" value={editForm.lastName} onChange={handleEditChange('lastName')} fullWidth />
          </Row>
          <Row>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select label="Gender" value={editForm.gender} onChange={handleEditChange('gender')}>
                {GENDER_OPTIONS.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Signature" value={editForm.signature} onChange={handleEditChange('signature')} fullWidth />
          </Row>
          <Row>
            <TextField label="Mobile Phone" value={editForm.phoneNo_Mobile} onChange={handleEditChange('phoneNo_Mobile')} fullWidth />
            <TextField label="Home Phone" value={editForm.phoneNo_Home} onChange={handleEditChange('phoneNo_Home')} fullWidth />
          </Row>
        </Section>

        <Section title="Address">
          <Row>
            <TextField label="Address" value={editForm.address} onChange={handleEditChange('address')} fullWidth />
            <TextField label="Town / City" value={editForm.town} onChange={handleEditChange('town')} fullWidth />
          </Row>
          <Row>
            <TextField label="Country" value={editForm.country} onChange={handleEditChange('country')} fullWidth />
          </Row>
        </Section>

        <Section title="Family Information">
          <Row>
            <TextField label="Father's Name" value={editForm.fatherName} onChange={handleEditChange('fatherName')} fullWidth />
            <TextField label="Mother's Name" value={editForm.motherName} onChange={handleEditChange('motherName')} fullWidth />
          </Row>
          <Row>
            <TextField
              label="Gross Household Income (PKR)"
              type="number"
              value={editForm.presentHouseholdIncome}
              onChange={handleEditChange('presentHouseholdIncome')}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Please enter the total income of all earning members of the household, including the parent(s).">
                      <InfoOutlined sx={{ fontSize: 18, color: 'rgba(0, 0, 0, 0.54)', cursor: 'help' }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Total Family Members"
              type="number"
              value={editForm.totalFamilyMembers}
              onChange={handleEditChange('totalFamilyMembers')}
              fullWidth
            />
          </Row>
          <Row>
            <FormControl fullWidth>
              <InputLabel>Orphan</InputLabel>
              <Select label="Orphan" value={editForm.orphan} onChange={handleEditChange('orphan')}>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Row>
        </Section>

        <Section title="Reference 1">
          <Row>
            <TextField label="Name" value={editForm.referenceName1} onChange={handleEditChange('referenceName1')} fullWidth />
            <TextField label="Email" value={editForm.referenceEmail1} onChange={handleEditChange('referenceEmail1')} fullWidth />
          </Row>
          <Row>
            <TextField label="Phone" value={editForm.referencePhone1} onChange={handleEditChange('referencePhone1')} fullWidth />
          </Row>
        </Section>

        <Section title="Reference 2">
          <Row>
            <TextField label="Name" value={editForm.referenceName2} onChange={handleEditChange('referenceName2')} fullWidth />
            <TextField label="Email" value={editForm.referenceEmail2} onChange={handleEditChange('referenceEmail2')} fullWidth />
          </Row>
          <Row>
            <TextField label="Phone" value={editForm.referencePhone2} onChange={handleEditChange('referencePhone2')} fullWidth />
          </Row>
        </Section>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
        <ButtonMain variant="white" onClick={handleClose}>
          Close
        </ButtonMain>
        <ButtonMain onClick={handleSave} disabled={editSaving}>
          Save
        </ButtonMain>
      </DialogActions>
    </Dialog>
  );
};

export default EditStudentModal;
